// Utility functions specific to sharing functionality

/**
 * Generate share URL for a plan (optionally with ?lang= for plan language)
 * @param {string} planId - The plan ID (shareId)
 * @param {Object} options - Additional options: domain, language (plan language code for ?lang=)
 * @returns {string} - Full share URL
 */
export const generateShareUrl = (planId, options = {}) => {
  const baseUrl = options.domain || (typeof window !== 'undefined' ? window.location.origin : '');
  const lang = options.language || '';
  const path = `/share/plan/${planId}`;
  return lang ? `${baseUrl}${path}?lang=${encodeURIComponent(lang)}` : `${baseUrl}${path}`;
};

/**
 * Copy share URL to clipboard
 * @param {string} planId - The plan ID
 * @returns {Promise<boolean>} - Success status
 */
export const copyShareUrlToClipboard = async (planId) => {
  try {
    const shareUrl = generateShareUrl(planId);
    await navigator.clipboard.writeText(shareUrl);
    return true;
  } catch (error) {
    console.error('Failed to copy share URL:', error);
    return false;
  }
};

/**
 * Check if the current page is a share page
 * @returns {boolean} - True if on a share page
 */
export const isSharePage = () => {
  return window.location.pathname.startsWith('/share/');
};

/**
 * Get plan ID from current share URL
 * @returns {string|null} - Plan ID if on share page, null otherwise
 */
export const getPlanIdFromShareUrl = () => {
  if (!isSharePage()) return null;
  
  const pathParts = window.location.pathname.split('/');
  const planIndex = pathParts.indexOf('plan');
  
  if (planIndex !== -1 && planIndex + 1 < pathParts.length) {
    return pathParts[planIndex + 1];
  }
  
  return null;
};
